import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { serverNow, useCountdown } from '../../lib/clock'
import { lotteryPhaseAt, lotteryTimeline } from '../../lib/lotteryTimeline'
import type { LotteryPhase } from '../../lib/lotteryTimeline'
import type { DraftState, TeamState } from '../../types/protocol'
import { FlightLayer } from './flightLayer'
import { HopperSim, fitBallText } from './hopperSim'
import type { Pt } from './hopperSim'
import { useLotteryClock } from './useLotteryClock'

/* ---------------------------------------------------------------------------
 * The machine. A declarative rail, LED and meter driven off one LotteryPhase,
 * plus an imperative controller for the canvas hopper and the SVG flight layer.
 *
 * The seek is not implemented here — it is a consequence of lotteryPhaseAt
 * being pure. There is no catch-up path and no code that replays past beats
 * quickly: a latecomer's first frame evaluates the same function at a larger
 * `elapsed` and paints the result.
 * ------------------------------------------------------------------------ */

export interface LedFace {
  label: string
  value: string
  ghost: string
  tone: 'warm' | 'crit' | 'lock'
}

const pad2 = (n: number) => (n < 10 ? '0' : '') + n

// The sweeper runs at 1 Hz, so there can legitimately be up to a second between
// the countdown hitting zero and the anchor arriving. Pinning the LED to
// "Spinning up" makes that read as machinery spooling rather than as a stalled
// 00:00 — and when the anchor lands, lotteryPhaseAt returns `spinup` for the
// first 500ms anyway, so the handover is invisible.
const SPINUP_FACE: LedFace = {
  label: 'Spinning up',
  value: '- -',
  ghost: '8 8',
  tone: 'warm',
}

/**
 * The T-minus digits, escalating by magnitude. MM:SS alone is only honest
 * inside the hour: a draw set for next Tuesday is 9000 minutes out and renders
 * as "9000:14", a number no one reads as a date. Same objection formatLeadTime
 * (lib/datetime.ts) was written for, and this is its fixed-width LED sibling —
 * seconds survive into the hours because the machine is still a clock there,
 * and are dropped past a day, where a units digit flickering on a value that
 * moves once a minute is the tenths-of-a-second slot's mistake again.
 *
 * Exported for the same reason ledFace is: assertable without a DOM.
 */
export function ledCountdown(remainingMs: number): string {
  const total = Math.floor(remainingMs / 1000)
  const days = Math.floor(total / 86400)
  const hh = Math.floor((total % 86400) / 3600)
  const mm = Math.floor((total % 3600) / 60)
  const ss = total % 60
  if (days > 0) return `${days}d ${pad2(hh)}:${pad2(mm)}`
  if (hh > 0) return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`
  return `${pad2(mm)}:${pad2(ss)}`
}

/** The dark segments behind the value: the same glyphs with every digit lit.
 *  Derived rather than written down, because the countdown's width is no
 *  longer fixed and a ghost of the wrong length is a visibly misaligned LED. */
const ghostFor = (value: string) => value.replace(/[0-9]/g, '8')

/**
 * The readout, beat for beat from the mockup, minus its tenths-of-a-second
 * slot: a digit flickering ten times a second next to a clock that only ever
 * moves once a second reads as lag rather than as precision.
 *
 * Exported and pure so it is assertable without a DOM — the same split as
 * countdownFrom out of useCountdown, for the same reason.
 */
export function ledFace(phase: LotteryPhase | null, remainingMs: number | null): LedFace {
  if (!phase) {
    if (remainingMs == null || remainingMs <= 0) return SPINUP_FACE
    const value = ledCountdown(remainingMs)
    return {
      label: 'T-minus to draw',
      value,
      ghost: ghostFor(value),
      // The clock going critical. This is the one place in the lottery that
      // keeps --color-crit: it *is* a clock, so it is the existing meaning
      // rather than a new one.
      tone: remainingMs < 3000 ? 'crit' : 'warm',
    }
  }

  switch (phase.phase) {
    case 'pre':
    case 'spinup':
      return SPINUP_FACE
    case 'finale':
      return { label: 'Last ball in the tank', value: '01', ghost: '88', tone: 'crit' }
    case 'crown':
    case 'done':
      return { label: 'Order locked', value: 'LOCK', ghost: '8888', tone: 'lock' }
    default:
      return {
        label: 'Now drawing pick',
        value: phase.ledPick == null ? '- -' : pad2(phase.ledPick),
        ghost: '88',
        tone: 'warm',
      }
  }
}

// Agitation multiplier, from the mockup: 2.9 spinning up, 1.35 through the
// draw, 3.7 for the rattle before the last ball dropping to 1.25 once it is in
// flight, halted at the crown.
function boostFor(p: LotteryPhase): number {
  switch (p.phase) {
    case 'spinup':
      return 2.9
    case 'drawing':
      return 1.35
    case 'finale':
      return p.flight ? 1.25 : 3.7
    default:
      return 0
  }
}

/** A ball's exit point, normalised to the glass box at capture time.
 *
 *  Absolute stage pixels would leave a 150ms funnel arc starting from where the
 *  hopper *used* to be after a rotation — the ball teleporting sideways, once.
 *  The hopper has moved and the ball is already out of the sim by then, so there
 *  is nothing to re-ask; storing the fraction and resolving it every frame is
 *  what survives a re-measure. */
interface FromGlass {
  fx: number
  fy: number
}

interface Rig {
  hopper: HopperSim
  flight: FlightLayer
  labels: string[]
  flying: number | null
  from: FromGlass | null
  lastElapsed: number
}

export default function LotteryStage({ draft }: { draft: DraftState }) {
  const teamCount = draft.teams.length
  const timeline = useMemo(() => lotteryTimeline(Math.max(2, teamCount)), [teamCount])
  const revealedAt = draft.lottery_revealed_at
  const lotteryAtMs = draft.lottery_at ? Date.parse(draft.lottery_at) : null
  const { remainingMs } = useCountdown(revealedAt ? null : lotteryAtMs)

  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const funnelRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLDivElement>(null)
  const socketRefs = useRef<HTMLElement[]>([])
  const rigRef = useRef<Rig | null>(null)

  const onFrame = useCallback((elapsedMs: number, p: LotteryPhase) => {
    const rig = rigRef.current
    const stage = stageRef.current
    const canvas = canvasRef.current
    if (!rig || !stage || !canvas) return
    // .lot-cv is inset:0 inside .lot-glass, so the canvas's own rect is the
    // glass box — the same fact HopperSim's constructor relies on.
    const stageBox = stage.getBoundingClientRect()
    const glassBox = canvas.getBoundingClientRect()
    // Clamped: a tab restore hands us a multi-second jump, and feeding that to a
    // rigid-body integrator tunnels every ball straight through the glass.
    const dtMs = Math.min(50, Math.max(0, elapsedMs - rig.lastElapsed))
    rig.lastElapsed = elapsedMs
    rig.hopper.boost = boostFor(p)
    rig.hopper.step(dtMs / 1000) // step() is in seconds; the timeline is in ms
    rig.hopper.draw()
    // A ball leaves the hopper when the timeline says so, never when the flight
    // layer feels like it — durations are pure, geometry only decides where.
    //
    // Every landed pick, not just the one in flight. `elapsed` can leap several
    // seconds in a single tick — a phone tab restored mid-draw re-anchors on
    // visibilitychange — and any pick whose whole flight window fell inside that
    // jump would otherwise still be churning behind the glass while its row sits
    // filled in the rail and the pip meter, both of which derive from `phase`
    // and are already correct. remove() is a no-op once a body is gone, and the
    // flying pick is never in `landed`, so this is safe every frame.
    for (const pick of p.landed) rig.hopper.remove(pick)

    // remove() hands back the body's centre in stage coordinates, which is the
    // one thing the flight layer takes from the sim.
    if (p.flight && p.flight.pick !== rig.flying) {
      rig.flying = p.flight.pick
      const at = rig.hopper.remove(p.flight.pick)
      rig.from =
        at && glassBox.width && glassBox.height
          ? {
              fx: (at.x - (glassBox.left - stageBox.left)) / glassBox.width,
              fy: (at.y - (glassBox.top - stageBox.top)) / glassBox.height,
            }
          : null
    }
    // Resolved against the *current* boxes, so a rotation mid-funnel moves the
    // arc's origin with the hopper instead of leaving it behind.
    const from: Pt | null = rig.from
      ? {
          x: glassBox.left - stageBox.left + rig.from.fx * glassBox.width,
          y: glassBox.top - stageBox.top + rig.from.fy * glassBox.height,
        }
      : null
    rig.flight.render({
      landed: p.landed,
      flight:
        p.flight && from
          ? { ...p.flight, from, label: rig.labels[p.flight.pick - 1] ?? '--' }
          : null,
    })
  }, [])

  const { phase, elapsedRef, mode, reduced, runId, replay } = useLotteryClock(
    revealedAt,
    timeline,
    onFrame,
  )

  // #77's arithmetic, resolved on the first frame and never revisited:
  //   elapsed0 < 0                     -> standby
  //   0 <= elapsed0 < timeline.crownMs -> running, mid-sequence, which is fine
  //   elapsed0 >= timeline.crownMs     -> settled, no animation at all
  // The last one is "don't seek into the last second of a 12.13s sequence":
  // arriving 11.5s in to watch the last ~600ms of the crown is worse than
  // arriving to a settled board.
  //
  // Resolved during the first render rather than from the first rAF frame,
  // because a frame later is too late: the imperative effect would already have
  // built a hopper and a flight layer for a page that is never going to animate,
  // and then torn them down. One serverNow() read on mount, latched — the same
  // discipline useLotteryClock applies to its own anchor.
  const settledRef = useRef<boolean | null>(null)
  if (settledRef.current === null && revealedAt) {
    const revealedAtMs = Date.parse(revealedAt)
    settledRef.current =
      !Number.isNaN(revealedAtMs) && serverNow() - revealedAtMs >= timeline.crownMs
  }
  const settled = settledRef.current === true
  // During a replay the page re-runs the draw locally, so the settled
  // short-circuit stands down: the rig gets built, the crown starts
  // un-crowned, and everything follows `phase` again.
  const settledView = settled && mode === 'sync'

  // Picks already landed on the first frame were seeked into, not watched. They
  // get data-filled and nothing else — no is-thunk on the row, no is-land on
  // .lot-ball, no is-go on .lot-ring. React makes that nearly free: a CSS
  // transition does not run on an element's first paint, so a row that mounts
  // already filled simply *is* filled.
  const seededRef = useRef<Set<number> | null>(null)
  const seededRunRef = useRef(-1)
  if (seededRunRef.current !== runId) {
    seededRunRef.current = runId
    seededRef.current = null
  }

  // No team names in the DOM before the reveal. The mockup builds every row with
  // its franchise already in the markup and hides it with clip-path; that is
  // fine where the order is a constant and wrong twice here — pre-reveal there
  // *is* no answer, and placeholder positions would be wrong data sitting where
  // view-source and a screen reader both find it.
  const order: TeamState[] | null = useMemo(
    () =>
      revealedAt
        ? [...draft.teams].sort((a, b) => (a.draft_position ?? 0) - (b.draft_position ?? 0))
        : null,
    [revealedAt, draft.teams],
  )

  useEffect(() => {
    // No hopper, no flight layer, no geometry pass. CSS cannot reach a canvas
    // or a rAF loop, so this early return is the whole of the imperative half
    // of the reduced-motion degradation.
    if (reduced) return
    if (!revealedAt || settledView || !order) return
    const canvas = canvasRef.current
    const stage = stageRef.current
    const svg = svgRef.current
    const funnel = funnelRef.current
    const rail = railRef.current
    if (!canvas || !stage || !svg || !funnel || !rail) return

    // The app is wrapped in <StrictMode>, so this effect runs, cleans up, and
    // runs again in dev. Both objects below own state tied to one canvas and one
    // <g>; skip the cleanup and dev gets two hoppers stepping one canvas and two
    // flight layers writing one group, at double speed, with no production
    // symptom to reproduce it from.
    //
    // `order` is read once here and never again; the hard lock freezes it from
    // lottery_revealed_at onward, which is why it stays out of the deps.
    // The flyer carries the whole franchise name. The rail's socket does not:
    // it is ~20px across in portrait, where a name would be a grey smudge, and
    // the row already spells it out in the next column.
    const labels = order.map((team) => team.name)
    const hopper = new HopperSim(canvas, stage)
    const flight = new FlightLayer({ stage, svg, funnel, rail, sockets: socketRefs.current })
    flight.measure()

    // Seed with only the remaining balls. remove(pick) is already the sim's
    // public API, so the seek needs nothing new from the renderer. The one
    // render is what paints a rig that is rebuilt after its loop has already
    // stopped — after a replay hands the anchor back, nothing else would.
    const seed = lotteryPhaseAt(elapsedRef.current, timeline)
    // Pour only when the viewer is here for the start of it. Someone opening
    // the link six seconds in should find a tank already churning, not watch
    // balls drop into a draw that is half over.
    hopper.init(
      labels.map((label, i) => ({ pick: i + 1, label })),
      { pour: elapsedRef.current < timeline.beats[0].startMs },
    )
    for (const pick of seed.landed) hopper.remove(pick)
    flight.render({ landed: seed.landed, flight: null })

    rigRef.current = {
      hopper,
      flight,
      labels,
      flying: null,
      from: null,
      lastElapsed: elapsedRef.current,
    }

    // Every position the flight layer knows is measured, so a resize mid-draw
    // leaves the tube pointing at where the nozzle used to be. Feature-checked:
    // jsdom does not implement it.
    let ro: ResizeObserver | null = null
    if (typeof window.ResizeObserver === 'function') {
      ro = new ResizeObserver(() => {
        hopper.resize()
        flight.measure()
      })
      ro.observe(stage)
    }

    return () => {
      rigRef.current = null
      ro?.disconnect()
      flight.destroy()
      hopper.destroy()
    }
    // `runId` is how a replay re-seeds the hopper with all N balls and clears
    // the flight layer: the effect tears down and rebuilds exactly once per
    // replay. It is a dependency rather than a `key` on the subtree, because a
    // key would remount the rail too — and the rail is the one thing that has
    // to survive a replay's start, so the rows visibly clear instead of the
    // whole composition flashing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealedAt, settledView, timeline, reduced, runId])

  // Whenever the live rig is *not* mounted, the machine still has to look like a
  // machine. The tube's `d`, the gutter, the ports and the nozzle are all
  // generated by measure(), so with no flight layer at all the plumbing is
  // simply absent — during the countdown the funnel opens onto nothing, which
  // is what a viewer sees for the whole minute before the draw. The mockup got
  // away with it only because its tube carried a hand-written literal `d`.
  //
  // One measure and one render. The expensive half — the canvas hopper and the
  // rAF loop — still never starts.
  const staticPaint = !reduced && !(revealedAt && !settledView && order)
  useEffect(() => {
    if (!staticPaint) return
    const stage = stageRef.current
    const svg = svgRef.current
    const funnel = funnelRef.current
    const rail = railRef.current
    if (!stage || !svg || !funnel || !rail) return

    const flight = new FlightLayer({ stage, svg, funnel, rail, sockets: socketRefs.current })
    const paint = () => {
      if (!flight.measure()) return
      // Empty before the draw, full after it. There is no third case: the rig
      // owns every instant in between.
      flight.render({
        landed: settledView ? Array.from({ length: teamCount }, (_, i) => i + 1) : [],
        flight: null,
      })
    }
    paint()

    let ro: ResizeObserver | null = null
    if (typeof window.ResizeObserver === 'function') {
      ro = new ResizeObserver(paint)
      ro.observe(stage)
    }

    return () => {
      ro?.disconnect()
      flight.destroy()
    }
  }, [staticPaint, settledView, teamCount])

  // The crown's stagger is choreography inside one beat, not a beat of its own —
  // nobody synchronises on it — so it is one timer rather than another field on
  // LotteryTimeline. The rest of the crown's internal timing already lives in
  // the keyframe delays.
  const [cracked, setCracked] = useState(false)
  useEffect(() => {
    if (settledView || phase?.phase === 'done') {
      setCracked(true)
      return
    }
    if (phase?.phase !== 'crown') return
    setCracked(false)
    const id = setTimeout(() => setCracked(true), 300)
    return () => clearTimeout(id)
  }, [phase?.phase, settledView])

  const sparks = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => {
        const ang = (i / 18) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
        const d = 2.4 + Math.random() * 3.6
        return {
          white: i % 3 === 0,
          x: `${(Math.cos(ang) * d).toFixed(2)}cqw`,
          y: `${(Math.sin(ang) * d).toFixed(2)}cqw`,
          r: `${Math.round(Math.random() * 360)}deg`,
          delay: `${Math.round(Math.random() * 110)}ms`,
        }
      }),
    [],
  )

  // A settled page has no rAF loop, so `phase` stays null and everything below
  // has to come from the timeline's end state instead of from a frame.
  const settledPhase = useMemo(
    () => (settledView ? lotteryPhaseAt(timeline.doneMs, timeline) : null),
    [settledView, timeline],
  )
  const shown = phase ?? settledPhase

  // Seeded from `shown` rather than from `phase`, so a settled page — which has
  // no rAF loop and therefore no phase — counts all twelve as already there
  // instead of animating pick 1 in on arrival. Why the set exists at all is on
  // its declaration above.
  if (shown && seededRef.current === null) seededRef.current = new Set(shown.landed)

  const led = ledFace(shown, remainingMs)
  const landed = new Set(shown?.landed ?? [])
  const remaining = shown ? shown.remaining.length : teamCount
  const crowned = settledView || shown?.phase === 'crown' || shown?.phase === 'done'
  const picks = Array.from({ length: teamCount }, (_, i) => i + 1)

  const status: { state: 'wait' | 'draw' | 'lock'; text: string } = crowned
    ? { state: 'lock', text: 'Order locked' }
    : shown
      ? { state: 'draw', text: 'Drawing' }
      : remainingMs != null && remainingMs < 3000
        ? { state: 'draw', text: 'Stand by' }
        : { state: 'wait', text: 'Standby' }

  // "Halted" would imply one had been running.
  const agitator = reduced
    ? 'Idle'
    : crowned
    ? 'Halted'
    : shown?.phase === 'spinup' || (shown?.phase === 'finale' && !shown.flight)
      ? 'Maximum'
      : 'Running'

  // Draw order is N down to 1, so the most recent landing is the lowest number
  // in `landed`. On a seek this jumps straight to the seeked-to pick and
  // announces only that, which is right — the earlier ones were never watched.
  const lastLanded = shown && shown.landed.length ? Math.min(...shown.landed) : null

  // The row's arrival flourish, and the only thing on this page that is a
  // one-shot rather than a function of `elapsed`. It has to expire: without a
  // timer the classes stick to the last row that landed for the life of the
  // page, which leaves is-thunk on the crown row forever. 620ms covers the
  // longest of the three keyframes (lottery-ring at .62s).
  const [justLanded, setJustLanded] = useState<number | null>(null)
  useEffect(() => {
    if (lastLanded == null) return
    if (mode === 'sync' && seededRef.current?.has(lastLanded)) return
    setJustLanded(lastLanded)
    const id = setTimeout(() => setJustLanded(null), 620)
    return () => clearTimeout(id)
  }, [lastLanded, mode])

  const announcement =
    lastLanded != null && order ? `Pick ${lastLanded}, ${order[lastLanded - 1]?.name ?? ''}` : ''

  return (
    <div ref={stageRef} className="lot-stage">
      <div className="lot-grid" />
      <div className="lot-floor" />

      <div className="lot-head">
        <div>
          <div className="lot-title">
            <i className="lot-chip" />
            Draft Order Lottery
          </div>
          <div className="lot-sub">
            {teamCount} franchises &middot; one random draw &middot; no takebacks
          </div>
        </div>
        <div className="lot-status" data-state={status.state}>
          <i className="lot-dot" />
          <span className="lot-status-t">{status.text}</span>
        </div>
      </div>

      <div className="lot-house" />
      <i className="lot-screw a" />
      <i className="lot-screw b" />
      <i className="lot-screw c" />
      <i className="lot-screw d" />

      <div className="lot-brand">
        <span className="lot-brand-n">Agitator 12-B</span>
        <span className="lot-brand-s">Order Selector</span>
      </div>

      <div className="lot-led" data-tone={led.tone}>
        <div className="lot-led-top">
          <span className="lot-led-label">{led.label}</span>
          <span className="lot-led-sys">Sys OK</span>
        </div>
        <div className="lot-led-row">
          {/* The bezel is fixed-width and clips; the value is not fixed-width
              any more. CSS sizes the digits off this count — see --led-fit. */}
          <span
            className="lot-led-slot"
            style={{ '--led-glyphs': String(led.value.length) } as CSSProperties}
          >
            <span className="lot-led-ghost">{led.ghost}</span>
            <span className="lot-led-val">{led.value}</span>
          </span>
        </div>
      </div>

      <div className="lot-glass">
        {/* The element itself, not just the flyer inside .lot-flight below:
            everything in that SVG is *positioned* by FlightLayer.measure(), so
            keeping it would mean keeping that geometry pass and its
            ResizeObserver alive to place decoration that will never move. The
            glass keeps its shell, gloss and rim — that is the machine's body,
            and it is CSS. It is simply empty. */}
        {!reduced && <canvas ref={canvasRef} className="lot-cv" aria-hidden="true" />}
        <div className={remaining === 0 ? 'lot-empty is-on' : 'lot-empty'}>Empty</div>
        <div className="lot-gloss" />
        <div className="lot-rim" />
        <div className="lot-drain" />
      </div>
      <div ref={funnelRef} className="lot-funnel">
        <i className="lot-gate" />
      </div>

      <div className="lot-meter">
        <div className="lot-meter-top">
          <span className="lot-meter-l">Balls in play</span>
          <span className="lot-meter-v">{remaining}</span>
        </div>
        <div className="lot-pips">
          {picks.map((pick, i) => (
            <i key={pick} className={i < remaining ? 'lot-pip' : 'lot-pip is-out'} />
          ))}
        </div>
        <div className="lot-meter-foot">
          <span>Agitator</span>
          <i className={crowned || reduced ? 'lot-agit is-off' : 'lot-agit'} />
          <span className="lot-agit-t">{agitator}</span>
        </div>
      </div>

      {/* --teams goes on the rail, not on .lot-slots: --lot-row is declared on
          .lot-rail and a custom property is only visible to descendants of the
          element that declares it, so from up here .lot-slots is too low to be
          seen. Declared below, every row's height calc() resolves to the
          guaranteed-invalid value and the socket grows to fill its column. */}
      <div
        ref={railRef}
        className="lot-rail"
        style={{ '--teams': teamCount } as unknown as CSSProperties}
      >
        <div className="lot-rail-head">
          <span />
          <span className="lot-rh-pick">Pick</span>
          <span>Franchise</span>
          <span className="lot-rh-own">Owner</span>
        </div>
        {/* DOM order is pick 1..N and must stay that way — a screen reader reads
            the final draft order top to bottom. The bottom-up *visual* stacking
            is `justify-content: flex-end` on .lot-slots, not a reversed array.
            Do not reverse this map to "fix" the visual order. */}
        <div key={revealedAt ?? 'pending'} className="lot-slots">
          {picks.map((pick) => {
            const filled = landed.has(pick)
            const team = order?.[pick - 1] ?? null
            const isCrown = pick === 1 && crowned
            const isCrack = pick === 1 && cracked
            const fresh = justLanded === pick
            return (
              <div
                key={pick}
                className={[
                  'lot-slot',
                  isCrown ? 'is-crown' : '',
                  isCrack ? 'is-crack' : '',
                  fresh ? 'is-thunk' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-pick={pick}
                data-filled={filled ? '1' : undefined}
              >
                <div
                  className="lot-sock"
                  ref={(el) => {
                    if (el) socketRefs.current[pick - 1] = el
                  }}
                >
                  {/* The whole franchise name here too, not initials: same
                      break as the hopper sprite and the flyer, so the ball a
                      viewer watched fly is the ball they read in the row. The
                      two custom properties are what let CSS size it — it cannot
                      count characters, and the socket is a cq-unit box whose
                      pixel size React does not know. */}
                  <span
                    className={fresh ? 'lot-ball is-land' : 'lot-ball'}
                    style={
                      team
                        ? ({
                            '--ball-chars': fitBallText(team.name).chars,
                            '--ball-lines': fitBallText(team.name).lines.length,
                          } as unknown as CSSProperties)
                        : undefined
                    }
                  >
                    {team
                      ? fitBallText(team.name).lines.map((line, i) => (
                          <span key={i} className="lot-ball-t">
                            {line}
                          </span>
                        ))
                      : null}
                    {pick === 1 ? (
                      <>
                        <i className="lot-half l" />
                        <i className="lot-half r" />
                        <i className="lot-core">1</i>
                      </>
                    ) : null}
                  </span>
                  <i className={fresh ? 'lot-ring is-go' : 'lot-ring'} />
                </div>
                <div className="lot-num">{pad2(pick)}</div>
                <div className="lot-name">
                  <i />
                  <b>{team?.name ?? ''}</b>
                </div>
                <div className="lot-own">{team?.owner_name ?? ''}</div>
                {pick === 1 ? (
                  <>
                    <i className="lot-flash" />
                    <span className="lot-tag">1.01 on the clock</span>
                    {isCrack
                      ? sparks.map((s, i) => (
                          <i
                            key={i}
                            className={s.white ? 'lot-spark w' : 'lot-spark'}
                            style={
                              {
                                '--x': s.x,
                                '--y': s.y,
                                '--r': s.r,
                                animationDelay: s.delay,
                              } as CSSProperties
                            }
                          />
                        ))
                      : null}
                  </>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>

      {/* No viewBox here on purpose: measure() writes it from the stage's own
          box, which is what makes one composition correct at any aspect. */}
      {!reduced && (
        <svg ref={svgRef} className="lot-flight" aria-hidden="true">
        <defs>
          <radialGradient id="lottery-ballg" cx="34%" cy="26%" r="82%">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="0.5" stopColor="#f3f0e6" />
            <stop offset="1" stopColor="#c6bfae" />
          </radialGradient>
        </defs>
        <path className="lot-tube-wall" />
        <path className="lot-tube-bore" />
        <path className="lot-tube-lite" />
        <path className="lot-tube-dash" />
        <path className="lot-tube-flow" />
        <path className="lot-path" fill="none" stroke="none" />
        <line className="lot-gut" />
        <rect className="lot-gutfill" height="0" />
        <g className="lot-ports">
          {picks.map((pick) => (
            <circle key={pick} className="lot-port" />
          ))}
        </g>
        <rect className="lot-nozzle" />
        <circle className="lot-nozzle-lamp" />
        <g className="lot-trail">
          <circle className="lot-ghost" opacity="0" />
          <circle className="lot-ghost" opacity="0" />
          <circle className="lot-ghost" opacity="0" />
        </g>
        <g className="lot-flyer" opacity="0">
          <circle className="lot-fly-glow" />
          <circle className="lot-fly-body" />
          <circle className="lot-fly-ring" />
          <text className="lot-fly-txt">--</text>
        </g>
        </svg>
      )}

      {/* No cancel: the run is bounded at timeline.doneMs and ends itself, and
          a disabled button in the corner of a full-screen stage is noise. It is
          a local re-render of data the client already has — it broadcasts
          nothing, writes nothing, and is invisible to every other screen. */}
      {replay && (
        <button type="button" className="lot-replay" onClick={replay}>
          Replay
        </button>
      )}

      {/* The rail itself is not a live region: twelve rows flipping data-filled
          would queue twelve announcements and re-read the whole board on the
          last one. This announces the single most recent landing. aria-atomic so
          a partial diff is never spoken. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
    </div>
  )
}
